import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getSeerbitSettings, updateSeerbitSettings } from "../../../redux/actions/profile";

const PRIMARY = "#a7cc48";
const BG = "#2c2d3f";

const TIERS = [
  { key: "tier1", label: "Tier 1", hint: "BVN + NIN" },
  { key: "tier2", label: "Tier 2", hint: "+ Government ID" },
  { key: "tier3", label: "Tier 3", hint: "+ Proof of address" },
  { key: "business", label: "Business", hint: "Approved KYB" },
];
const FIELDS = [
  { key: "singleLimit", label: "Single transfer limit" },
  { key: "dailyLimit", label: "Daily transfer limit" },
  { key: "maxBalance", label: "Maximum balance" },
];

// Amounts are kobo on the API and naira in the form; blank = unlimited (null).
const toNaira = (k) => (k == null ? "" : String(k / 100));
const toKobo = (n) => (n === "" || n == null ? null : Math.round(Number(n) * 100));

export default function SeerbitSettings() {
  const dispatch = useDispatch();
  const [tiers, setTiers] = useState(null);
  const [bands, setBands] = useState([]);
  const [percent, setPercent] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const load = async () => {
    const res = await dispatch(getSeerbitSettings());
    if (!res?.success) return setMessage({ error: true, text: res?.message || "Unable to load settings" });
    const t = {};
    TIERS.forEach(({ key }) => {
      t[key] = {};
      FIELDS.forEach(({ key: f }) => { t[key][f] = toNaira(res.data.tiers?.[key]?.[f]); });
    });
    setTiers(t);
    setBands((res.data.providerFeeBands || []).map((b) => ({ upTo: toNaira(b.upTo), fee: toNaira(b.fee) })));
    setPercent(String(res.data.platformFeePercent ?? 50));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    setMessage(null);
    const body = {
      tiers: Object.fromEntries(
        TIERS.map(({ key }) => [key, Object.fromEntries(FIELDS.map(({ key: f }) => [f, toKobo(tiers[key][f])]))])
      ),
      providerFeeBands: bands.map((b) => ({ upTo: toKobo(b.upTo), fee: toKobo(b.fee) || 0 })),
      platformFeePercent: Number(percent),
    };
    const res = await dispatch(updateSeerbitSettings(body));
    setSaving(false);
    setMessage({ error: !res?.success, text: res?.message || (res?.success ? "Saved" : "Unable to save") });
    if (res?.success) load();
  };

  if (!tiers) {
    return <p style={{ color: "#888", padding: 24 }}>{message?.text || "Loading settings…"}</p>;
  }

  // Example fee shown to the user for the first band, so admin sees the effect of the %
  const example = bands[0] ? Number(bands[0].fee || 0) : 0;
  const exampleOurs = Math.round(example * Number(percent || 0)) / 100;

  return (
    <div style={{ paddingBottom: 40 }}>
      <h4 style={{ color: BG, fontWeight: 800, marginBottom: 4 }}>SeerBit Wallet Settings</h4>
      <p style={{ color: "#888", fontSize: 13, marginBottom: 20 }}>
        CBN tier limits and transfer fees. Amounts are in naira. Leave a field blank for no limit.
      </p>

      <div style={S.card}>
        <div style={S.cardTitle}>Tier limits</div>
        <div style={{ overflowX: "auto" }}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Tier</th>
                {FIELDS.map((f) => <th key={f.key} style={S.th}>{f.label} (₦)</th>)}
              </tr>
            </thead>
            <tbody>
              {TIERS.map((t) => (
                <tr key={t.key}>
                  <td style={S.td}>
                    <div style={{ fontWeight: 700, color: BG }}>{t.label}</div>
                    <div style={{ fontSize: 11, color: "#999" }}>{t.hint}</div>
                  </td>
                  {FIELDS.map((f) => (
                    <td key={f.key} style={S.td}>
                      <input
                        style={S.input}
                        type="number"
                        min="0"
                        placeholder="No limit"
                        value={tiers[t.key][f.key]}
                        onChange={(e) => setTiers({ ...tiers, [t.key]: { ...tiers[t.key], [f.key]: e.target.value } })}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p style={{ fontSize: 12, color: "#999", marginTop: 10 }}>
          A wallet whose balance goes above its maximum balance is frozen for transfers until the user upgrades.
        </p>
      </div>

      <div style={S.card}>
        <div style={S.cardTitle}>Transfer fees</div>
        <p style={{ fontSize: 13, color: "#666", marginTop: 0 }}>
          SeerBit's payout fee by amount. Excite's fee is a percentage of it, and the user sees only the combined fee.
        </p>
        <table style={{ ...S.table, maxWidth: 520 }}>
          <thead>
            <tr>
              <th style={S.th}>Transfers up to (₦)</th>
              <th style={S.th}>SeerBit fee (₦)</th>
              <th style={S.th} />
            </tr>
          </thead>
          <tbody>
            {bands.map((b, i) => (
              <tr key={i}>
                <td style={S.td}>
                  <input style={S.input} type="number" min="0" placeholder="Anything above" value={b.upTo}
                    onChange={(e) => setBands(bands.map((x, j) => (j === i ? { ...x, upTo: e.target.value } : x)))} />
                </td>
                <td style={S.td}>
                  <input style={S.input} type="number" min="0" value={b.fee}
                    onChange={(e) => setBands(bands.map((x, j) => (j === i ? { ...x, fee: e.target.value } : x)))} />
                </td>
                <td style={S.td}>
                  {bands.length > 1 && (
                    <button style={S.linkBtn} onClick={() => setBands(bands.filter((_, j) => j !== i))}>Remove</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button style={{ ...S.linkBtn, marginTop: 8 }} onClick={() => setBands([...bands, { upTo: "", fee: "" }])}>
          + Add band
        </button>

        <div style={{ marginTop: 20, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: BG }}>Excite fee (% of SeerBit fee)</label>
          <input style={{ ...S.input, width: 100 }} type="number" min="0" value={percent} onChange={(e) => setPercent(e.target.value)} />
          <span style={{ fontSize: 12, color: "#888" }}>
            e.g. ₦{example.toLocaleString()} SeerBit + ₦{exampleOurs.toLocaleString()} Excite = user pays ₦{(example + exampleOurs).toLocaleString()}
          </span>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <button style={S.primaryBtn} onClick={save} disabled={saving}>{saving ? "Saving…" : "Save settings"}</button>
        {message && <span style={{ fontSize: 13, color: message.error ? "#e63946" : "#2ec4b6" }}>{message.text}</span>}
      </div>
    </div>
  );
}

const S = {
  card: { background: "#fff", borderRadius: 10, padding: 20, boxShadow: "0 2px 10px rgba(0,0,0,0.06)", marginBottom: 20 },
  cardTitle: { fontSize: 15, fontWeight: 700, color: BG, marginBottom: 14 },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { background: BG, color: "#fff", padding: "10px 12px", textAlign: "left", fontSize: 12, fontWeight: 600 },
  td: { padding: "8px 12px", fontSize: 13, color: "#555", borderBottom: "1px solid #f5f5f5" },
  input: { border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "7px 10px", fontSize: 13, width: "100%", minWidth: 110, outline: "none" },
  linkBtn: { background: "none", border: "none", color: "#4fb0c6", cursor: "pointer", fontSize: 13, fontWeight: 600, padding: 0 },
  primaryBtn: { background: PRIMARY, color: "#fff", border: "none", borderRadius: 8, padding: "10px 22px", fontWeight: 700, cursor: "pointer" },
};
